import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, LinearProgress, Stack, TextField, Typography } from '@mui/material'
import { EditNote, AddCircleOutlineRounded, RemoveCircleOutlineRounded, Inventory2Outlined, WarningAmberRounded, HourglassTopOutlined, DeleteSweepOutlined, PlaylistAddCheckOutlined } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { apiGet, apiPut } from '../../generic/Api_Functions'

// S39/S40 (D10, historia 89): the two literals for the not-started cancellation limit. Two separate
// keys on purpose, no pluralisation — "Only 0 parts…" does not read well, so the zero case has its
// own sentence. The number always comes from the server field, never from parsing a literal.
const MAX_CANCELLABLE_MESSAGE = 'Only {{count}} parts have not been started yet and can be cancelled'
const NONE_CANCELLABLE_MESSAGE = 'No parts can be cancelled: all of them have already started'
// H156_S56/S57 (03-dominio.md §6): the deletion twins of the two literals above. Same shape, same
// reason for being two keys instead of one pluralised sentence, and a DIFFERENT server field.
const MAX_DELETABLE_MESSAGE = 'Only {{count}} parts have not been started yet and can be deleted'
const NONE_DELETABLE_MESSAGE = 'No parts can be deleted: all of them have already started'
// Absent field sentinel: any negative value means "this is not the not-started rejection" (S42).
const NO_LIMIT_REJECTION = -1

// H156 (03-dominio.md §6): the eight keys this story introduces, and not a ninth — a missing count
// shows no reason at all (RN18.1), so there is nothing to name.
const DELETE_SURPLUS_LABEL = 'Delete surplus parts'
const PARTS_WILL_BE_DELETED = '{{count}} parts will be deleted'
const NOT_STARTED_COUNT = '{{count}} parts have not been started yet'
const CHECKING_NOT_STARTED = 'Checking parts not started yet'
const NOT_STARTED_ONLY_REASON = 'Deletion is only possible for parts that have not been started'
const NO_DELETE_PERMISSION_REASON = 'You do not have permission to delete parts'

/**
 * H156_S50 (RN9.1 / DL6): the screen checks the very same permission literal the server checks
 * (`SurplusPartsDisposition.requiredAuthority()`). `DELETE_PART_INSTANCE` — the code the Parts screen
 * uses for its own delete button — does NOT take part in this story; mixing them is the mismatch
 * this story refuses to repeat.
 */
const DELETE_PARTS_AUTHORITY = 'PART_INSTANCES'

/**
 * S42 (D11 / R4b): the new presentation branch is entered ONLY when the error body carries the max
 * cancellable count. Api_Functions keeps the whole error body in `error.data`, so the field arrives
 * untouched. Any other failure (open-regime 422, network error, 500, an older server that does not
 * publish the field) must keep today's behaviour: close the dialog and raise the generic alert.
 */
const readMaxCancellableParts = (error) => {
  const max = error && error.data ? error.data.maxCancellableParts : NO_LIMIT_REJECTION
  return Number.isInteger(max) && max >= 0 ? max : NO_LIMIT_REJECTION
}

/**
 * H156_S58 (R4): the deletion rejection is recognised by the presence of its OWN field. The two
 * names are deliberately distinct (`maxDeletableParts` vs `maxCancellableParts`) so each rejection
 * composes its own message; a body carrying neither keeps today's default — close and alert.
 */
const readMaxDeletableParts = (error) => {
  const max = error && error.data ? error.data.maxDeletableParts : NO_LIMIT_REJECTION
  return Number.isInteger(max) && max >= 0 ? max : NO_LIMIT_REJECTION
}

/**
 * S22-S26 / S32 (MIC-025-02, #23): "Modify quantity" modal for a fabrication order, extracted from
 * FabricationOrder so the OF table component stays focused. Owns the whole interaction:
 *  - the form dialog (absolute new quantity + optional reason) with a live created/cancelled preview;
 *  - the D3 two-step confirmation dialog when the reduction would cancel already-OK parts;
 *  - the S32 determinate progress bar for a large INCREASE (background creation + polling).
 *
 * Props:
 *  - fabricationOrder: the OF row being modified ({ id, quantity, fabricationOrder }); null when closed.
 *  - open: whether the form dialog is open.
 *  - onClose: called to close the form dialog (parent clears its open state).
 *  - onCompleted: called once the quantity has actually changed (sync success, or the background
 *    creation reached COMPLETED/FAILED). onClose alone cannot tell: it fires as soon as the
 *    progress bar starts, before a single part exists.
 *  - showAlert: alert/toast function (severity/title/message), injected so the parent owns the UI slot.
 */
const ModifyQuantityDialog = ({ fabricationOrder, open, onClose, onCompleted = () => {}, showAlert }) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()

  const [form, setForm] = useState({ newQuantity: '', reason: '' })
  const [submitting, setSubmitting] = useState(false)
  // H156_S39 (CA9 / D8): the two counts published by the reduction preview. `available` is what
  // tells apart "the server said zero" from "we could not ask", which RN18.1 treats differently.
  const [preview, setPreview] = useState({ available: false, cancelablePartCount: 0, notStartedPartCount: 0 })
  const [previewLoading, setPreviewLoading] = useState(false)
  // H156_S42 (D1 / RN18): the operator's choice of destination. Born unchecked, and it dies with the
  // operation — it is never remembered between attempts.
  const [deleteSurplusParts, setDeleteSurplusParts] = useState(false)
  const [confirmCancelDialog, setConfirmCancelDialog] = useState({
    open: false, newQuantity: 0, reason: '', okPartsToCancel: 0
  })
  // S39-S41 (D11): max cancellable parts published by the last not-started rejection. Kept as data
  // so the message is composed at render time; NO_LIMIT_REJECTION means "no rejection to show".
  const [maxCancellableParts, setMaxCancellableParts] = useState(NO_LIMIT_REJECTION)
  // H156_S56/S57: the same, for the deletion rejection. Its own field, its own pair of literals.
  const [maxDeletableParts, setMaxDeletableParts] = useState(NO_LIMIT_REJECTION)
  // S32: background parts-creation progress bar (INCREASE) + its polling interval handle.
  const [progress, setProgress] = useState({
    open: false, taskId: null, percent: 0, created: 0, total: 0, state: 'RUNNING'
  })
  const pollRef = useRef(null)

  // Refs mirror the current row/form so the async submit reads the latest values even through the
  // SmartDialog boundary (the accept callback may be captured once), avoiding stale closures.
  const rowRef = useRef(null)
  const formRef = useRef({ newQuantity: '', reason: '' })
  // H156_S54/S61: the destination that will actually travel in the body, mirrored so the async
  // submit reads the effective choice even through the SmartDialog callback boundary.
  const deleteSurplusRef = useRef(false)

  // When the dialog opens, seed the form from the current quantity and load the reduction preview,
  // so the screen can show how many parts have not been started yet (H156_S39/S40).
  useEffect(() => {
    if (!open || !fabricationOrder) return
    const initial = { newQuantity: String(fabricationOrder.quantity || ''), reason: '' }
    rowRef.current = fabricationOrder
    formRef.current = initial
    setForm(initial)
    setMaxCancellableParts(NO_LIMIT_REJECTION)
    setMaxDeletableParts(NO_LIMIT_REJECTION)
    setDeleteSurplusParts(false)
    setPreview({ available: false, cancelablePartCount: 0, notStartedPartCount: 0 })
    setPreviewLoading(true)
    loadReductionPreview(fabricationOrder.id)
  }, [open, fabricationOrder])

  // Always clear the progress polling interval on unmount.
  useEffect(() => {
    return () => stopPolling()
  }, [])

  /**
   * H156_S39 (D8 / R12): the ONE query the dialog makes when it opens. It SUBSTITUTES the former
   * `fabricationOrderPartProgress/{id}` call — that endpoint is untouched and still serves the parts
   * progress dialog, but this screen no longer derives "not started" from the RAW/WORKING/FINISHED
   * split, which is exactly the misunderstanding this story closes (RN21, F1).
   *
   * H156_S41/S52 (RN18.1): it is a convenience, never a guard. A failure leaves `available` false,
   * the counts off screen and the checkbox usable — the server is the one that cuts (E3).
   */
  const loadReductionPreview = async (fabricationOrderId) => {
    try {
      const counts = await apiGet('fabrication-orders/' + fabricationOrderId + '/reduction-preview')
      setPreview({
        available: true,
        cancelablePartCount: Number(counts?.cancelablePartCount) || 0,
        notStartedPartCount: Number(counts?.notStartedPartCount) || 0
      })
    } catch (error) {
      setPreview({ available: false, cancelablePartCount: 0, notStartedPartCount: 0 })
    } finally {
      setPreviewLoading(false)
    }
  }

  // How many parts a given typed quantity would take out of the order (0 when it is not a reduction).
  const partsToRemoveFor = (value) => {
    const parsed = Number(value)
    if (value === '' || Number.isNaN(parsed) || parsed <= 0) return 0
    const difference = parsed - Number(rowRef.current ? rowRef.current.quantity : 0)
    return difference < 0 ? Math.abs(difference) : 0
  }

  /**
   * H156_S48/S49 (DL1 / RN19): REACTIVE, not static — the criterion is the quantity typed right now
   * (`parts to remove > parts not started`), never "this order has some started part". H156_S50: the
   * permission reason wins over it. H156_S52 (RN18.1): with no counts at hand the screen does NOT
   * decide, so it stays permissive and lets the server cut.
   */
  const deletionDisabledReasonFor = (value) => {
    if (!isUserAuthorized(DELETE_PARTS_AUTHORITY)) return NO_DELETE_PERMISSION_REASON
    const toRemove = partsToRemoveFor(value)
    if (preview.available && toRemove > preview.notStartedPartCount) return NOT_STARTED_ONLY_REASON
    return ''
  }

  // Single source of truth for the new-quantity field (TextField onChange + test harness).
  const handleFieldChange = (value) => {
    const next = { ...formRef.current, newQuantity: String(value) }
    formRef.current = next
    setForm(next)
    // H156_S47 (RN18 / CU-D A3): the choice dies with the operation. A quantity that stops offering
    // the deletion drops it, so the checkbox can only ever come back unchecked.
    if (partsToRemoveFor(value) === 0 || deletionDisabledReasonFor(value) !== '') {
      setDeleteSurplusParts(false)
    }
  }

  // S22/S23/S32: submit the modify-quantity request (first pass or confirmed second pass).
  const handleSubmit = async (confirmCancelFinishedParts = false) => {
    const row = rowRef.current
    const currentForm = formRef.current
    if (!row) return
    const newQuantity = Number(currentForm.newQuantity)
    const currentQuantity = Number(row.quantity)

    // S32 (CA13 / DA5): a plain INCREASE (no OK-parts confirmation) can create many parts and is
    // served by the async progress endpoint. Reduce / no-op / confirmed second pass keep the
    // classic synchronous PUT (v1, S22-S25).
    if (!confirmCancelFinishedParts && newQuantity > currentQuantity) {
      await startWithProgress(row, newQuantity, currentForm.reason)
      return
    }

    // H156_S54/S61 (INV8): the checkbox decides this one field and nothing else travels — no count,
    // no maximum, nothing derived from the preview. The screen may be wrong; the result may not.
    const body = {
      newQuantity,
      reason: currentForm.reason,
      confirmCancelFinishedParts,
      deleteSurplusParts: deleteSurplusRef.current
    }
    setSubmitting(true)
    // S41: each attempt resolves on its own; drop the previous rejection before re-sending.
    setMaxCancellableParts(NO_LIMIT_REJECTION)
    setMaxDeletableParts(NO_LIMIT_REJECTION)
    try {
      const result = await apiPut('fabrication-orders/' + row.id + '/quantity', body)
      // S23: backend may require explicit confirmation before cancelling OK parts.
      if (result && result.type === 'CONFIRMATION_REQUIRED') {
        setConfirmCancelDialog({
          open: true,
          newQuantity,
          reason: currentForm.reason,
          okPartsToCancel: result.okPartsToCancel || 0
        })
        return
      }
      // S22/S25: success (including no-op 200).
      onClose()
      onCompleted()
      showAlert({ severity: 'success', title: t_('Quantity modified successfully'), message: '' })
    } catch (error) {
      // H156_S56-S58 (R4): the deletion branch is entered if and ONLY if its own field arrives. It
      // sits beside the #89 branch without touching it, and neither changes the default below.
      const maxDeletable = readMaxDeletableParts(error)
      if (maxDeletable !== NO_LIMIT_REJECTION) {
        setMaxDeletableParts(maxDeletable)
        return
      }
      // S39-S41 (D11): only the not-started rejection keeps the dialog open, with the typed quantity
      // preserved and the max in sight, so correcting the number and accepting again is enough.
      const maxCancellable = readMaxCancellableParts(error)
      if (maxCancellable !== NO_LIMIT_REJECTION) {
        setMaxCancellableParts(maxCancellable)
        return
      }
      // S42 / H156_S59: everything else degrades exactly as before — close and alert with the
      // server literal, which is also its i18n fallback key.
      onClose()
      showAlert({ severity: 'error', title: t_('Error modifying quantity'), message: t_(error.message) })
    } finally {
      setSubmitting(false)
    }
  }

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  // S32 (CA13 / DA5): kick off the background creation, get the taskId and open the progress bar.
  const startWithProgress = async (row, newQuantity, reason) => {
    setSubmitting(true)
    try {
      const { taskId } = await apiPut('fabrication-orders/' + row.id + '/quantity/progress-start', {
        newQuantity,
        reason,
        confirmCancelFinishedParts: false
      })
      onClose()
      setProgress({ open: true, taskId, percent: 0, created: 0, total: 0, state: 'RUNNING' })
      pollProgress(taskId)
    } catch (error) {
      onClose()
      showAlert({ severity: 'error', title: t_('Error modifying quantity'), message: t_(error.message) })
    } finally {
      setSubmitting(false)
    }
  }

  // S32: poll the progress endpoint on an interval, updating the bar. Stops and reports success on
  // COMPLETED, error on FAILED. Never mutates the row locally.
  const pollProgress = (taskId) => {
    stopPolling()
    const tick = async () => {
      try {
        const snapshot = await apiGet('fabrication-orders/parts-creation-progress/' + taskId)
        setProgress((prev) => ({
          ...prev,
          percent: snapshot.percent ?? prev.percent,
          created: snapshot.created ?? prev.created,
          total: snapshot.total ?? prev.total,
          state: snapshot.state ?? prev.state
        }))
        if (snapshot.state === 'COMPLETED') {
          stopPolling()
          setProgress((prev) => ({ ...prev, open: false }))
          onCompleted()
          showAlert({ severity: 'success', title: t_('Quantity modified successfully'), message: '' })
        } else if (snapshot.state === 'FAILED') {
          stopPolling()
          setProgress((prev) => ({ ...prev, open: false }))
          // A part may already have been created before the failure.
          onCompleted()
          showAlert({ severity: 'error', title: t_('Error modifying quantity'), message: t_('Parts creation failed') })
        }
      } catch (error) {
        stopPolling()
        setProgress((prev) => ({ ...prev, open: false }))
        // A part may already have been created before the poll broke.
        onCompleted()
        showAlert({ severity: 'error', title: t_('Error modifying quantity'), message: t_('Parts creation failed') })
      }
    }
    pollRef.current = setInterval(tick, 1000)
  }

  // S23: user confirms cancellation of OK parts — resend with flag=true.
  // S46 (D15): this second step deliberately does NOT share the not-started branch above. It only
  // exists with the control OFF, so getting that rejection here means a configuration race; it
  // degrades exactly as today (close + generic alert with the server literal).
  const handleConfirmCancelAccept = async () => {
    const row = rowRef.current
    const { newQuantity, reason } = confirmCancelDialog
    setConfirmCancelDialog({ open: false, newQuantity: 0, reason: '', okPartsToCancel: 0 })
    if (!row) return
    const body = { newQuantity, reason, confirmCancelFinishedParts: true }
    setSubmitting(true)
    try {
      await apiPut('fabrication-orders/' + row.id + '/quantity', body)
      onClose()
      onCompleted()
      showAlert({ severity: 'success', title: t_('Quantity modified successfully'), message: '' })
    } catch (error) {
      showAlert({ severity: 'error', title: t_('Error modifying quantity'), message: t_(error.message) })
    } finally {
      setSubmitting(false)
      onClose()
    }
  }

  // S39/S40 (D10): compose the rejection text from the published max — two separate keys, picked by
  // the value itself, so the zero case never reads "Only 0 parts…".
  const hasCancellationLimit = maxCancellableParts !== NO_LIMIT_REJECTION
  const cancellationLimitMessage = maxCancellableParts > 0
    ? t_(MAX_CANCELLABLE_MESSAGE, { count: maxCancellableParts })
    : t_(NONE_CANCELLABLE_MESSAGE)

  // H156_S56/S57: compose the deletion rejection from its own published max, by the value itself.
  const hasDeletionLimit = maxDeletableParts !== NO_LIMIT_REJECTION
  const deletionLimitMessage = maxDeletableParts > 0
    ? t_(MAX_DELETABLE_MESSAGE, { count: maxDeletableParts })
    : t_(NONE_DELETABLE_MESSAGE)

  // Live preview of the effect of the entered quantity (created vs cancelled parts).
  const currentQuantity = Number(fabricationOrder?.quantity) || 0
  const parsed = Number(form.newQuantity)
  const hasValidInput = form.newQuantity !== '' && !Number.isNaN(parsed) && parsed > 0
  const delta = hasValidInput ? parsed - currentQuantity : 0
  const toCancel = delta < 0 ? Math.abs(delta) : 0
  // Only reserve the right-hand preview column once there is something to show.
  const showChips = hasValidInput && delta !== 0
  const isReduction = showChips && delta < 0

  // H156_S42/S48-S53 (RN18): the checkbox is shown on every reduction, disabled with its reason when
  // it cannot be used, and NEVER hidden. `deletionRequested` is the effective choice: a checkbox that
  // turned disabled after being ticked can never leak a deletion into the request.
  const deletionDisabledReason = deletionDisabledReasonFor(form.newQuantity)
  const deletionRequested = deleteSurplusParts && deletionDisabledReason === ''

  // Keep the submit mirror in step with the effective choice, so an accept callback captured earlier
  // still sends exactly what the screen is showing.
  useEffect(() => { deleteSurplusRef.current = deletionRequested })

  /**
   * H156_S44 (RN21 / DL3 — closes F1): how many of the parts to remove are already in production,
   * derived from the counts the server publishes: `min(max(0, toRemove − notStarted), cancelable −
   * notStarted)`. It NO LONGER comes from the RAW/WORKING/FINISHED split, because `RAW` never meant
   * "nobody touched it". With no counts at hand the warning is omitted, never estimated (RN18.1).
   */
  const inProductionToCancel = preview.available
    ? Math.min(
      Math.max(0, toCancel - preview.notStartedPartCount),
      Math.max(0, preview.cancelablePartCount - preview.notStartedPartCount)
    )
    : 0
  // H156_S45: with the deletion chosen the warning makes no sense — a reduction reaching a started
  // part would have disabled the checkbox in the first place.
  const showInProductionWarning = !deletionRequested && inProductionToCancel > 0
  // H156_S40/S41 (RN17 / DL2): the count (or its loading chip) is on screen from the very opening,
  // with nothing typed and whether or not it restricts anything.
  const showPreviewColumn = showChips || previewLoading || preview.available
  // Let the preview chip labels wrap instead of truncating in the narrow column.
  const chipSx = {
    height: 'auto',
    maxWidth: '100%',
    py: 0.4,
    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3 }
  }

  return (
    <>
      {/* S22-S26: Modify quantity form dialog */}
      <SmartDialog
        setOpen={open}
        accept
        cancel
        close={false}
        disableButtons={submitting}
        title={createDialogTitle({ title: t_('Modify fabrication order quantity'), icon: EditNote })}
        acceptCallback={() => handleSubmit(false)}
        cancelCallback={() => onClose()}
        onNewQuantityChange={handleFieldChange}
        renderComponent={
          submitting
            ? (
              <Stack alignItems='center' justifyContent='center' spacing={2} sx={{ py: 5, minWidth: '22rem' }}>
                <CircularProgress size={52} thickness={4} />
                <Typography sx={{ color: 'text.secondary' }}>{t_('Applying quantity change, please wait')}</Typography>
              </Stack>
              )
            : (
              <Stack spacing={2.5} sx={{ mt: 1, minWidth: '38rem' }}>
                {/* S39-S41 (D11): the not-started rejection stays here, with the dialog open and the
                    typed quantity untouched, so the retry is a single gesture. */}
                {hasCancellationLimit
                  ? (
                    <Alert severity='warning' sx={{ textAlign: 'left' }}>
                      {cancellationLimitMessage}
                    </Alert>
                    )
                  : null}
                {/* H156_S56/S57/S64: the deletion rejection behaves the same way — the dialog stays
                    open with the typed quantity, so correcting the number and accepting is enough. */}
                {hasDeletionLimit
                  ? (
                    <Alert severity='warning' sx={{ textAlign: 'left' }}>
                      {deletionLimitMessage}
                    </Alert>
                    )
                  : null}
                <Stack
                  direction='row' spacing={1} alignItems='center' justifyContent='center'
                  sx={theme => ({
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: theme.palette.action.hover
                  })}
                >
                  <Inventory2Outlined fontSize='small' color='primary' />
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('Current quantity')}:</Typography>
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>{currentQuantity}</Typography>
                </Stack>
                <Divider flexItem />
                <Stack direction='row' spacing={2} alignItems='center'>
                  <Box sx={{ flex: showPreviewColumn ? 3 : 6 }}>
                    <TextField
                      fullWidth
                      autoFocus
                      label={t_('New quantity')}
                      type='number'
                      inputProps={{ min: 1 }}
                      value={form.newQuantity}
                      onChange={(e) => handleFieldChange(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ flex: showPreviewColumn ? 4 : 6 }}>
                    <TextField
                      fullWidth
                      label={t_('Reason (optional)')}
                      value={form.reason}
                      onChange={(e) => {
                        const next = { ...formRef.current, reason: e.target.value }
                        formRef.current = next
                        setForm(next)
                      }}
                    />
                  </Box>
                  {/* Live preview chips, to the right of the reason field, stacked and centered */}
                  {showPreviewColumn
                    ? (
                      <Box sx={{ flex: 5 }}>
                        <Stack spacing={1} alignItems='center'>
                          {/* H156_S40/S41: the not-started count leads the column from the opening —
                              its loading chip first, then the number, and nothing at all if the
                              read failed, because there is no number to show. */}
                          {previewLoading
                            ? (
                              <Chip
                                variant='outlined'
                                sx={chipSx}
                                icon={<CircularProgress size={14} thickness={5} />}
                                label={t_(CHECKING_NOT_STARTED)}
                              />
                              )
                            : preview.available
                              ? (
                                <Chip
                                  color='info'
                                  variant='outlined'
                                  sx={chipSx}
                                  icon={<PlaylistAddCheckOutlined />}
                                  label={t_(NOT_STARTED_COUNT, { count: preview.notStartedPartCount })}
                                />
                                )
                              : null}
                          {showChips && delta > 0
                            ? (
                              <Chip
                                color='success'
                                variant='outlined'
                                sx={chipSx}
                                icon={<AddCircleOutlineRounded />}
                                label={t_('{{count}} parts will be created', { count: delta })}
                              />
                              )
                            : null}
                          {isReduction
                            ? (
                              <>
                                {/* H156_S43 (RN20): the chip tells the truth about the chosen
                                    destination, and it changes in the very gesture of ticking. */}
                                {deletionRequested
                                  ? (
                                    <Chip
                                      color='error'
                                      variant='outlined'
                                      sx={chipSx}
                                      icon={<DeleteSweepOutlined />}
                                      label={t_(PARTS_WILL_BE_DELETED, { count: toCancel })}
                                    />
                                    )
                                  : (
                                    <Chip
                                      color='warning'
                                      variant='outlined'
                                      sx={chipSx}
                                      icon={<RemoveCircleOutlineRounded />}
                                      label={t_('{{count}} parts will be cancelled', { count: toCancel })}
                                    />
                                    )}
                                {showInProductionWarning
                                  ? (
                                    <Chip
                                      color='error'
                                      variant='outlined'
                                      sx={chipSx}
                                      icon={<HourglassTopOutlined />}
                                      label={t_('{{count}} of them are already in production', { count: inProductionToCancel })}
                                    />
                                    )
                                  : null}
                              </>
                              )
                            : null}
                        </Stack>
                      </Box>
                      )
                    : null}
                </Stack>
                {/* H156_S42/S48-S53 (D1 / RN18 / DL7): the checkbox names the destructive action, is
                    born unchecked and, when it cannot be used, is shown DISABLED with its reason
                    beside it — never hidden, never let through to fail after accepting. */}
                {isReduction
                  ? (
                    <Stack spacing={0.5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={deletionRequested}
                            disabled={deletionDisabledReason !== ''}
                            onChange={(e) => setDeleteSurplusParts(e.target.checked)}
                          />
                        }
                        label={t_(DELETE_SURPLUS_LABEL)}
                      />
                      {deletionDisabledReason !== ''
                        ? (
                          <Typography variant='caption' sx={{ color: 'text.secondary', pl: 4, textAlign: 'left' }}>
                            {t_(deletionDisabledReason)}
                          </Typography>
                          )
                        : null}
                    </Stack>
                    )
                  : null}
              </Stack>
              )
        }
      />
      {/* S23: Confirmation dialog for cancelling OK/finished parts */}
      <SmartDialog
        setOpen={confirmCancelDialog.open}
        accept
        cancel
        close={false}
        title={createDialogTitle({ color: 'warning', title: t_('Cancel processed parts?'), icon: WarningAmberRounded })}
        message={t_('{{count}} parts already processed OK would be cancelled. Do you want to continue?', { count: confirmCancelDialog.okPartsToCancel })}
        acceptCallback={handleConfirmCancelAccept}
        cancelCallback={() => setConfirmCancelDialog({ open: false, newQuantity: 0, reason: '', okPartsToCancel: 0 })}
      />
      {/* S32 (CA13 / DA5): determinate progress bar while parts are created in the background.
          The user waits with the bar open; it is not resumable if closed (out of scope this version). */}
      <SmartDialog
        data-testkey='partsCreationProgress'
        setOpen={progress.open}
        accept={false}
        cancel={false}
        close={false}
        percent={progress.percent}
        state={progress.state}
        title={createDialogTitle({ title: t_('Creating parts'), icon: EditNote })}
        renderComponent={
          <Stack spacing={2} sx={{ py: 3, minWidth: '24rem' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              {t_('Creating parts')} {progress.percent}%
            </Typography>
            <LinearProgress variant='determinate' value={progress.percent} />
          </Stack>
        }
      />
    </>
  )
}

export default ModifyQuantityDialog
